/**
 * AppTile (spec 015 · mockup-faithful).
 *
 * Matches tmp/platform-mockups/launcher.html:
 *   - Top gradient rule (.app-tile::after) that slides in on hover
 *   - Logo mark (44px) + live-status pill with pulsing dot
 *   - Display-font name with italic accent on second word
 *   - Muted description, circular arrow button that fills on hover
 *   - Per-app accent colours via .t-chat / .t-news / .t-surf / .t-tea / .t-terminal
 *
 * All styles live in src/styles/launcher-home.css.
 */
import type { ComponentType } from 'react';
import { ArrowRight } from 'lucide-react';

import type { App } from '@/services/apps';
import { cn } from '@/lib/utils';
import {
  BitzBuggyLogo,
  LocalTeaLogo,
  NerdyBotLogo,
  SmartNewsLogo,
  SurfTimeLogo,
  TradingAgentLogo,
} from '@shared/platform/logos';

interface AppTileProps {
  app: App;
  onClick?: (app: App) => void;
}

interface LogoProps {
  size?: number;
  className?: string;
  title?: string;
}

// Map the app's route_path to its canonical logo.
const LOGO_BY_SLUG: Record<string, ComponentType<LogoProps>> = {
  'llm-chat': NerdyBotLogo,
  'smart-news': SmartNewsLogo,
  surftime: SurfTimeLogo,
  localtea: LocalTeaLogo,
  'trading-agent': TradingAgentLogo,
  bitzbuggy: BitzBuggyLogo,
};

// Drive the app-tile tint classes (defines --tile-accent / --tile-accent-2).
const TINT_BY_SLUG: Record<string, string> = {
  'llm-chat': 't-chat',
  'smart-news': 't-news',
  surftime: 't-surf',
  localtea: 't-tea',
  'trading-agent': 't-terminal',
  bitzbuggy: 't-bitzbuggy',
};

// Short editorial copy accents — second token rendered italic via <em>.
// The raw description stays authoritative; this only colors the name when
// backend copy is short enough to preserve the italic play.
function splitNameForItalic(name: string): [string, string | null] {
  const tokens = name.trim().split(/\s+/);
  if (tokens.length < 2) return [name, null];
  const first = tokens[0];
  const rest = tokens.slice(1).join(' ');
  return [first, rest];
}

function resolveLogo(app: App): ComponentType<LogoProps> | null {
  if (app.route_path && LOGO_BY_SLUG[app.route_path]) return LOGO_BY_SLUG[app.route_path];
  if (app.theme_id && LOGO_BY_SLUG[app.theme_id]) return LOGO_BY_SLUG[app.theme_id];
  return null;
}

function resolveTint(app: App): string {
  if (app.route_path && TINT_BY_SLUG[app.route_path]) return TINT_BY_SLUG[app.route_path];
  if (app.theme_id && TINT_BY_SLUG[app.theme_id]) return TINT_BY_SLUG[app.theme_id];
  return '';
}

function resolveStatus(app: App): { label: string; data: 'live' | 'coming-soon' | 'admin' } {
  if (app.status === 'coming_soon') return { label: 'Coming Soon', data: 'coming-soon' };
  // Admin-gated apps (Trading Agent) display a muted pill; heuristic on theme.
  if (app.route_path === 'trading-agent') return { label: 'admin only', data: 'admin' };
  return { label: 'online', data: 'live' };
}

export default function AppTile({ app, onClick }: AppTileProps) {
  const isActive = app.status === 'active';
  const Logo = resolveLogo(app);
  const tintClass = resolveTint(app);
  const status = resolveStatus(app);
  const [firstWord, restWords] = splitNameForItalic(app.name);

  const handleClick = () => {
    if (isActive && onClick) onClick(app);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!isActive}
      className={cn('app-tile', tintClass)}
      aria-label={app.status === 'coming_soon' ? `${app.name} (Coming Soon)` : app.name}
    >
      <div className="app-tile__head">
        <span className="app-tile__mark" aria-hidden="true">
          {Logo ? <Logo size={44} title={app.name} /> : <span>◆</span>}
        </span>
        <span className="app-tile__live-dot" data-status={status.data}>
          {status.label}
        </span>
      </div>

      <div className="app-tile__body">
        <h3 className="app-tile__name">
          {restWords ? (
            <>
              {firstWord} <em>{restWords}</em>
            </>
          ) : (
            firstWord
          )}
        </h3>
        {app.description && <p className="app-tile__desc">{app.description}</p>}
      </div>

      <span className="app-tile__arrow" aria-hidden="true">
        <ArrowRight />
      </span>
    </button>
  );
}
